import React, { useState } from 'react'
import { Download, Copy } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx'

interface DraftBackgroundDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

const DraftBackgroundDisplay: React.FC<DraftBackgroundDisplayProps> = ({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const isDarkMode = theme === 'dark'

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast?.('Failed to copy to clipboard');
    }
  };

  // Parse JSON if result is a string
  let parsedResult = result
  if (typeof result === 'string') {
    try {
      parsedResult = JSON.parse(result)
    } catch (e) {
      // If parsing fails, return null for self-detection
      return null;
    }
  }

  // Handle both direct result and nested output structure
  const background = parsedResult?.output || parsedResult

  // Self-detection: check if this looks like a draft background
  const isDraftBackground = background && typeof background === 'object' && (
    background.field_description ||
    background.prior_art_discussion ||
    background.technical_problems ||
    background.limitations_prior_art ||
    background.industry_context
  );

  if (!isDraftBackground) {
    return null; // Not a draft background, let other components handle it
  }

  if (!background || typeof background !== 'object') {
    return null;
  }

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const sectionConfigs = {
    field_description: { 
      title: 'Field Description', 
      emoji: '🔬', 
      type: 'text',
      description: 'Overview of the technological field'
    },
    prior_art_discussion: { 
      title: 'Prior Art Discussion', 
      emoji: '📚', 
      type: 'array',
      description: 'Existing solutions and approaches'
    },
    technical_problems: { 
      title: 'Technical Problems', 
      emoji: '⚠️', 
      type: 'array',
      description: 'Issues with current technology'
    },
    limitations_prior_art: { 
      title: 'Limitations of Prior Art', 
      emoji: '🚫', 
      type: 'array',
      description: 'Shortcomings of existing solutions'
    },
    industry_context: { 
      title: 'Industry Context', 
      emoji: '🏭', 
      type: 'text',
      description: 'Market and industry perspective'
    },
    regulatory_considerations: { 
      title: 'Regulatory Considerations', 
      emoji: '📋', 
      type: 'array',
      description: 'Compliance and regulatory factors'
    },
    safety_considerations: { 
      title: 'Safety Considerations', 
      emoji: '⚡', 
      type: 'array',
      description: 'Safety-related factors'
    },
    economic_factors: { 
      title: 'Economic Factors', 
      emoji: '💰', 
      type: 'array',
      description: 'Cost and economic considerations'
    }
  }

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Draft Background",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Generate sections
      Object.entries(sectionConfigs).forEach(([sectionKey, config]) => {
        const value = background[sectionKey];

        // Skip empty sections
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return;
        }

        // Section heading
        children.push(
          new Paragraph({
            text: config.title,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          })
        );

        if (config.type === 'text' && value) {
          children.push(
            new Paragraph({
              text: value,
              spacing: { after: 200, line: 360 },
            })
          );
        } else if (config.type === 'array' && Array.isArray(value) && value.length > 0) {
          value.forEach((item: any) => {
            if (typeof item === 'object') {
              // Special formatting for structured objects like prior art references
              if (item.reference && item.description) {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: item.reference, bold: true }),
                    ],
                    spacing: { before: 100, after: 50 },
                    indent: { left: 360 },
                  })
                );
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Description: ', bold: true }),
                      new TextRun({ text: item.description }),
                    ],
                    spacing: { after: 50 },
                    indent: { left: 360 },
                  })
                );
                if (item.limitations) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Limitations: ', bold: true }),
                        new TextRun({ text: item.limitations }),
                      ],
                      spacing: { after: 50 },
                      indent: { left: 360 },
                    })
                  );
                }
                if (item.relevance) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Relevance: ', bold: true }),
                        new TextRun({ text: item.relevance }),
                      ],
                      spacing: { after: 50 },
                      indent: { left: 360 },
                    })
                  );
                }
                if (item.teaching) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Teaching: ', bold: true }),
                        new TextRun({ text: item.teaching }),
                      ],
                      spacing: { after: 100 },
                      indent: { left: 360 },
                    })
                  );
                }
              } else {
                // Fallback for other object structures
                const itemText = JSON.stringify(item, null, 2);
                children.push(
                  new Paragraph({
                    text: `• ${itemText}`,
                    spacing: { after: 100 },
                    indent: { left: 360 },
                  })
                );
              }
            } else {
              // Simple string items
              children.push(
                new Paragraph({
                  text: `• ${item}`,
                  spacing: { after: 100 },
                  indent: { left: 360 },
                })
              );
            }
          });
        }
      });

      // Check if we have content
      const hasContent = Object.entries(sectionConfigs).some(([sectionKey, config]) => {
        const value = background[sectionKey];
        return value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0));
      });

      if (!hasContent) {
        children.push(
          new Paragraph({
            text: "No background content available for export",
            italics: true,
            spacing: { before: 200 },
          })
        );
      }

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'draft-background.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Draft Background exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Draft Background to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Draft Background');
      }
    }
  }

  // Helper function to format array values for copying/text export
  const formatArrayForText = (value) => {
    if (!Array.isArray(value)) return value?.toString() || '';
    
    return value.map(item => {
      if (typeof item === 'object' && item.reference && item.description) {
        // Format structured prior art references
        let formatted = `${item.reference}\nDescription: ${item.description}`;
        if (item.limitations) formatted += `\nLimitations: ${item.limitations}`;
        if (item.relevance) formatted += `\nRelevance: ${item.relevance}`;
        if (item.teaching) formatted += `\nTeaching: ${item.teaching}`;
        return formatted;
      } else if (typeof item === 'object') {
        // Fallback for other objects
        return JSON.stringify(item, null, 2);
      } else {
        // Simple string items
        return item;
      }
    }).join('\n\n');
  };

  const formatFieldValue = (field, value, config) => {
    if (!value) return 'Not specified'
    
    if (config.type === 'array') {
      if (!Array.isArray(value) || value.length === 0) return 'None specified'
      return (
        <ul className="list-disc pl-5 mt-1 space-y-2">
          {value.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {typeof item === 'object' ? (
                // Special formatting for structured objects like prior art references
                item.reference && item.description ? (
                  <div className="space-y-1">
                    <div className={`font-semibold text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {item.reference}
                    </div>
                    <div className="text-sm">
                      <strong>Description:</strong> {item.description}
                    </div>
                    {item.limitations && (
                      <div className="text-sm">
                        <strong>Limitations:</strong> {item.limitations}
                      </div>
                    )}
                    {item.relevance && (
                      <div className="text-sm">
                        <strong>Relevance:</strong> {item.relevance}
                      </div>
                    )}
                    {item.teaching && (
                      <div className="text-sm">
                        <strong>Teaching:</strong> {item.teaching}
                      </div>
                    )}
                  </div>
                ) : (
                  // Fallback for other object structures
                  <div className="text-sm font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded">
                    {JSON.stringify(item, null, 2)}
                  </div>
                )
              ) : (
                // Simple string items
                item
              )}
            </li>
          ))}
        </ul>
      )
    }
    
    if (config.type === 'text') {
      return <div className="leading-relaxed">{value}</div>
    }
    
    return value
  }

  const renderSection = (sectionKey, config) => {
    const value = background[sectionKey]
    const hasData = value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0))
    
    if (!hasData) return null

    const isExpanded = expandedSections[sectionKey] !== false // Default to expanded

    return (
      <div key={sectionKey} className={`mb-6 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{config.emoji}</span>
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {config.description}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between mb-2">
                  <span className={`text-sm font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                    {config.title}
                  </span>
                  <button
                    onClick={() => copyToClipboard(config.type === 'array' && Array.isArray(value) ? formatArrayForText(value) : value.toString())}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    <Copy className="w-3 h-3" />
                    Copy Field
                  </button>
                </div>
                <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {formatFieldValue(sectionKey, value, config)}
                </div>
              </div>
            </div>

            {/* Section Actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => copyToClipboard(`${config.title}:\n${config.type === 'array' && Array.isArray(value) ? formatArrayForText(value) : value}`)}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
              {addToResources && (
                <button
                  onClick={() => {
                    const sectionText = `# ${config.title}\n\n${config.type === 'array' && Array.isArray(value) ? formatArrayForText(value) : value}`
                    addToResources({
                      type: 'background_section',
                      title: config.title,
                      content: sectionText,
                      source: 'Draft Background Task'
                    })
                    showToast?.('Section added to resources!')
                  }}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-100 hover:bg-green-200 text-green-800'}`}
                >
                  Add to Resources
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Get sections with data
  const sectionsWithData = Object.keys(sectionConfigs).filter(sectionKey => {
    const value = background[sectionKey]
    const config = sectionConfigs[sectionKey]
    return value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0))
  })
  
  const allSectionsText = Object.entries(sectionConfigs)
    .filter(([sectionKey, config]) => {
      const value = background[sectionKey]
      return value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0))
    })
    .map(([sectionKey, config]) => {
      const value = background[sectionKey]
      const formattedValue = config.type === 'array' && Array.isArray(value) ? formatArrayForText(value) : value
      return `# ${config.title}\n\n${formattedValue}`
    })
    .join('\n\n---\n\n')

  return (
    <div className="space-y-4">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📖</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Draft Background
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {sectionsWithData.length} section{sectionsWithData.length !== 1 ? 's' : ''} with content
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
              <button
                onClick={exportToDocx}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                title="Export background as Word document"
              >
                <Download className="w-4 h-4" />
                Export DOCX
              </button>
              <button
                onClick={() => copyToClipboard(allSectionsText)}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-4 h-4" />
                Copy All
              </button>
            </div>
          </div>

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {}
                sectionsWithData.forEach(sectionKey => {
                  allExpanded[sectionKey] = true
                })
                setExpandedSections(allExpanded)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {}
                sectionsWithData.forEach(sectionKey => {
                  allCollapsed[sectionKey] = false
                })
                setExpandedSections(allCollapsed)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {Object.entries(sectionConfigs).map(([sectionKey, config]) => renderSection(sectionKey, config))}

        {/* No content message */}
        {sectionsWithData.length === 0 && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No background sections contain data to display.
          </div>
        )}
      </div>
    </div>
  )
}

export default DraftBackgroundDisplay